import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Link,
  CircularProgress,
  Alert,
  Stack,
  Tabs,
  Tab,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

const QUALIFICATIONS = [
  '10th pass',
  '12th pass',
  'ITI',
  'Diploma',
  'Graduate',
];

const API_URL = process.env.REACT_APP_API_URL;

// Teal theme – matches Welcome/Admin
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: '#f8fafc',
    '&:hover': { bgcolor: '#f1f5f9' },
    '&.Mui-focused': { bgcolor: '#fff' },
    '&.Mui-focused fieldset': { borderColor: '#0d9488', borderWidth: 2 },
    '&:hover fieldset': { borderColor: 'rgba(13, 148, 136, 0.4)' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#0d9488' },
};

const selectSx = {
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0d9488' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(13, 148, 136, 0.4)' },
};

const primaryBtnSx = {
  background: 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)',
  color: '#fff',
  borderRadius: 2,
  py: 1.25,
  fontWeight: 600,
  boxShadow: '0 4px 14px rgba(13, 148, 136, 0.35)',
  '&:hover': {
    background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)',
    boxShadow: '0 6px 20px rgba(13, 148, 136, 0.4)',
  },
};

function CandidateAuth({ onClose, onAuthSuccess, defaultStep = 'login' }) {
  const [step, setStep] = useState(defaultStep);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [qualification, setQualification] = useState('');
  const [course, setCourse] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [resendCount, setResendCount] = useState(0);
  const [resendTimer, setResendTimer] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [pendingMobile, setPendingMobile] = useState('');
  const [referralCode, setReferralCode] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const sendOtp = async (forReset = false) => {
    setLoading(true);
    setError('');
    try {
      const endpoint = forReset ? '/api/candidate/forgot-password' : '/api/candidate/resend-otp';
      const res = await fetch(API_URL + endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send OTP');
      setStep(forReset ? 'forgot-otp' : 'otp');
      setResendTimer(60);
      setResendCount(resendCount + 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (!name || !email || !mobile || !qualification || !course || !password || !confirmPwd) {
      setError('Please fill all fields');
      return;
    }
    if (password !== confirmPwd) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(API_URL + '/api/candidate/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, mobile, qualification, course, password, referral_code: referralCode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Signup failed');
      setStep('otp');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^[0-9]{10}$/.test(mobile)) return setError('Enter a valid 10-digit mobile number');
    if (!password) return setError('Enter your password');
    setLoading(true);
    try {
      const res = await fetch(API_URL + '/api/candidate/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile, password }),
      });
      
      if (!res.ok) {
        let data;
        try {
          data = await res.json();
        } catch (e) {
          throw new Error(`Server error: ${res.status}`);
        }
        
        if (data.error && data.error.toLowerCase().includes('not verified')) {
          setPendingVerification(true);
          setPendingMobile(mobile);
          setStep('otp-resend');
          setError('Account not verified. Please verify your number.');
        } else if (res.status === 400) {
          setError(data.error || 'Invalid mobile number or password.');
        } else {
          setError(data.error || `Login failed: ${res.status}`);
        }
        return;
      }
      
      const data = await res.json();
      let token = data.token || data.access || 'dummy-token';
      let user = data.user || { ...data, type: 'candidate' };
      localStorage.setItem('gogig_token', token);
      localStorage.setItem('gogig_user', JSON.stringify(user));
      if (onAuthSuccess) onAuthSuccess(token, user);
      navigate('/candidate/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      if (err.message === 'Failed to fetch' || err.name === 'TypeError') {
        setError('Cannot connect to server. Please ensure the backend server is running on port 5001.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^[0-9]{6}$/.test(otp)) return setError('Enter a valid 6-digit OTP');
    setLoading(true);
    try {
      const res = await fetch(API_URL + '/api/candidate/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile, otp }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'OTP verification failed');
      if (onAuthSuccess) onAuthSuccess(data.token, data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{10}$/.test(mobile)) return setError('Enter a valid 10-digit mobile number');
    await sendOtp(true);
  };

  const handleForgotOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(otp)) return setError('Enter a valid 6-digit OTP');
    setStep('reset');
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    if (!password || password.length < 6) return setError('Password must be at least 6 characters');
    setLoading(true);
    try {
      const res = await fetch(API_URL + '/api/candidate/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile, otp, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Reset failed');
      alert('Password reset!');
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (step === 'otp-resend') {
    return (
      <div className="auth-modal-wrap">
        {onClose && (
          <IconButton className="auth-modal-close" onClick={onClose} size="small" aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
        <Typography variant="h6" align="center" sx={{ color: '#0d9488', fontWeight: 700, mb: 2 }}>
          Verify Your Number
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
        <form onSubmit={handleOtp}>
          <Stack spacing={2}>
            <TextField fullWidth label="OTP" value={otp} onChange={(e) => setOtp(e.target.value)} inputProps={{ maxLength: 6 }} sx={inputSx} className="auth-input" />
            <Button fullWidth variant="contained" type="submit" disabled={loading} sx={primaryBtnSx}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify OTP'}
            </Button>
            {resendCount < 3 ? (
              <Button variant="outlined" disabled={resendTimer > 0} onClick={async () => { setMobile(pendingMobile); await sendOtp(); }} sx={{ borderColor: '#0d9488', color: '#0d9488', fontWeight: 600 }}>
                {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
              </Button>
            ) : (
              <Typography color="text.secondary" align="center">Resend locked. Try after 15 min.</Typography>
            )}
          </Stack>
        </form>
      </div>
    );
  }

  return (
    <div className="auth-modal-wrap">
      {onClose && (
        <IconButton className="auth-modal-close" onClick={onClose} size="small" aria-label="Close">
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      )}
      <Typography className="auth-modal-title">Candidate</Typography>
      <Typography className="auth-modal-subtitle">Sign in or create an account</Typography>
      <Tabs
        value={step === 'signup' ? 1 : 0}
        onChange={(e, v) => setStep(v === 1 ? 'signup' : 'login')}
        centered
        className="auth-tabs"
        sx={{
          mb: 2,
          '& .MuiTabs-indicator': { background: 'linear-gradient(90deg, #0d9488 0%, #2dd4bf 100%)', height: 3 },
          '& .MuiTab-root': { fontWeight: 600, textTransform: 'none' },
          '& .Mui-selected': { color: '#0d9488 !important' },
        }}
      >
        <Tab label="Login" />
        <Tab label="Sign Up" />
      </Tabs>

      {step === 'signup' && (
        <form onSubmit={handleSignup}>
          <Stack spacing={2}>
            <TextField fullWidth label="Name" value={name} onChange={(e) => setName(e.target.value)} sx={inputSx} className="auth-input" />
            <TextField fullWidth label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} sx={inputSx} className="auth-input" />
            <TextField fullWidth label="Mobile Number" value={mobile} onChange={(e) => setMobile(e.target.value)} inputProps={{ maxLength: 10 }} sx={inputSx} className="auth-input" />
            <FormControl fullWidth>
              <InputLabel id="qualification-label" sx={{ '&.Mui-focused': { color: '#0d9488' } }}>Qualification</InputLabel>
              <Select labelId="qualification-label" value={qualification} label="Qualification" onChange={(e) => setQualification(e.target.value)} sx={{ ...selectSx, borderRadius: 2, bgcolor: '#f8fafc' }}>
                {QUALIFICATIONS.map((q) => <MenuItem key={q} value={q}>{q}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField fullWidth label="Course" value={course} onChange={(e) => setCourse(e.target.value)} sx={inputSx} className="auth-input" />
            <TextField fullWidth label="Referral Code (optional)" value={referralCode} onChange={(e) => setReferralCode(e.target.value)} sx={inputSx} className="auth-input" />
            <TextField fullWidth label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} sx={inputSx} className="auth-input" />
            <TextField fullWidth label="Confirm Password" type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} sx={inputSx} className="auth-input" />
            {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
            <Button fullWidth variant="contained" type="submit" disabled={loading} sx={primaryBtnSx}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign Up'}
            </Button>
          </Stack>
        </form>
      )}

      {step === 'login' && (
        <form onSubmit={handleLogin}>
          <Stack spacing={2}>
            <TextField fullWidth label="Mobile Number" value={mobile} onChange={(e) => setMobile(e.target.value)} inputProps={{ maxLength: 10 }} sx={inputSx} className="auth-input" />
            <TextField fullWidth label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} sx={inputSx} className="auth-input" />
            {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
            <Button fullWidth variant="contained" type="submit" disabled={loading} sx={primaryBtnSx}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
            </Button>
            <Link component="button" variant="body2" onClick={() => setStep('forgot')} sx={{ color: '#0d9488', fontWeight: 600, textAlign: 'center' }} className="auth-link">
              Forgot Password?
            </Link>
          </Stack>
        </form>
      )}

      {step === 'otp' && (
        <form onSubmit={handleOtp}>
          <Stack spacing={2}>
            <TextField fullWidth label="OTP" value={otp} onChange={(e) => setOtp(e.target.value)} inputProps={{ maxLength: 6 }} sx={inputSx} className="auth-input" />
            {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
            <Button fullWidth variant="contained" type="submit" disabled={loading} sx={primaryBtnSx}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify OTP'}
            </Button>
            {resendCount < 3 ? (
              <Button variant="outlined" disabled={resendTimer > 0} onClick={() => sendOtp()} sx={{ borderColor: '#0d9488', color: '#0d9488', fontWeight: 600 }}>
                {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
              </Button>
            ) : (
              <Typography color="text.secondary" align="center">Resend locked. Try after 15 min.</Typography>
            )}
          </Stack>
        </form>
      )}

      {step === 'forgot' && (
        <form onSubmit={handleForgot}>
          <Stack spacing={2}>
            <TextField fullWidth label="Mobile Number" value={mobile} onChange={(e) => setMobile(e.target.value)} inputProps={{ maxLength: 10 }} sx={inputSx} className="auth-input" />
            {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
            <Button fullWidth variant="contained" type="submit" disabled={loading} sx={primaryBtnSx}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send OTP'}
            </Button>
          </Stack>
        </form>
      )}

      {step === 'forgot-otp' && (
        <form onSubmit={handleForgotOtp}>
          <Stack spacing={2}>
            <TextField fullWidth label="OTP" value={otp} onChange={(e) => setOtp(e.target.value)} inputProps={{ maxLength: 6 }} sx={inputSx} className="auth-input" />
            {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
            <Button fullWidth variant="contained" type="submit" disabled={loading} sx={primaryBtnSx}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify OTP'}
            </Button>
          </Stack>
        </form>
      )}

      {step === 'reset' && (
        <form onSubmit={handleReset}>
          <Stack spacing={2}>
            <TextField fullWidth label="New Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} sx={inputSx} className="auth-input" />
            {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
            <Button fullWidth variant="contained" type="submit" disabled={loading} sx={primaryBtnSx}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
            </Button>
          </Stack>
        </form>
      )}
    </div>
  );
}

export default CandidateAuth;